import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { map, Observable, switchMap } from 'rxjs';
import { filetypeextension, filetypemime } from 'magic-bytes.js';
import { MimeTypes } from '../constants/mime-types';

@Injectable({
	providedIn: 'root'
})
export class FileUtility {
	public saveAsExcel(data: Blob, fileName: string): void {
		const blob = new Blob([data],
			{
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});
		saveAs(blob, fileName);
	}

	public saveAsAnything(data: Blob, contentType: string, fileName?: string): void {
		const blob = new Blob([data],
			{
				type: contentType
			});
		saveAs(blob, fileName);
	}

	public createChunks(file, sizeInBytes)  {
		let startPointer = 0;
		const endPointer = file.size;
		const chunks = [];
		while (startPointer < endPointer) {
			const newStartPointer = startPointer + sizeInBytes;
			chunks.push(file.slice(startPointer, newStartPointer));
			startPointer = newStartPointer;
		}
		return chunks;
	}

	public getTypeAndExtensionInfo$(file: File): Observable<FileMetadata> {
		return new Observable((observer) => {
			if (file.type) {
				observer.next({ file, mime: file.type, ext: null });
				observer.complete();
				return;
			}

			const fileReader = new FileReader();

			fileReader.addEventListener('loadend', event => {
				const input = event.target.result;

				if (!(input instanceof Uint8Array || input instanceof ArrayBuffer)) {
					observer.next({ file, mime: file.type, ext: null });
					observer.complete();
					return;
				}

				const buffer = input instanceof Uint8Array ? input : new Uint8Array(input);
				const mime = filetypemime(buffer as any);
				const ext = filetypeextension(buffer as any);
				observer.next({ file, mime: mime[0], ext: ext[0] });
				observer.complete();
			});

			fileReader.readAsArrayBuffer(file);
		}).pipe(
			map((result: FileMetadata) => {
				if(!result?.mime) {
					result.ext = result.ext || result.file.name.split('.').pop();
					result.mime = this.resolveMimeTypeUsingMimeMap(result.ext);
				}
				return result;
			})
		);
	}

	public getFileMetadata$(file: FileSystemFileEntry): Observable<FileMetadata> {
		const observable$ = new Observable<File>((observer) => {
			file.file((rawFile) => {
				observer.next(rawFile);
				observer.complete();
			});
		});
		return observable$.pipe(
			switchMap((result) => {
				return this.getTypeAndExtensionInfo$(result);
			}),
			map((result) => {
				return { relativePath: file.fullPath, ...result };
			})
		);
	}

	public async getAllFileEntries(dataTransferItemList: DataTransferItemList) {
		const fileEntries = [];
		const queue = [];
		for (let i = 0; i < dataTransferItemList.length; i++) {
			queue.push(dataTransferItemList[i].webkitGetAsEntry());
		}
		while (queue.length > 0) {
			const entry = queue.shift();
			if(!entry) {
				throw new Error('Unable to retrieve file entry.');
			}
			if (entry.isFile) {
				fileEntries.push(entry);
			} else if (entry.isDirectory) {
				queue.push(...await this.readAllDirectoryEntries(entry.createReader()));
			}
		}
		return fileEntries;
	}

	public async readAllDirectoryEntries(directoryReader: FileSystemDirectoryReader) {
		const entries = [];
		let readEntries = await this.readEntriesPromise(directoryReader);
		while ((readEntries as any).length > 0) {
			entries.push(...readEntries as any);
			readEntries = await this.readEntriesPromise(directoryReader);
		}
		return entries;
	}

	public async readEntriesPromise(directoryReader) {
		try {
			return new Promise((resolve, reject) => {
				directoryReader.readEntries(resolve, reject);
			});
		} catch (err) {
			console.error(err);
		}
	}

	public resolveMimeTypeUsingMimeMap(extension: string): string {
		const key = `.${extension}`
		const result = MimeTypes.get(key);
		return result;
	}
}

export interface FileMetadata {
	file: File;
	ext: string;
	mime: string;
	relativePath?: string;
}
